import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';

const RASTER_W = 1280;
const RASTER_H = 1024;
const INNER_BUILD_VERSION = '20260528-1635';

const withInnerBuildVersion = (rawUrl: string) => {
    try {
        const url = new URL(rawUrl, window.location.href);
        if (!url.searchParams.has('v')) {
            url.searchParams.set('v', INNER_BUILD_VERSION);
        }
        return url.pathname + url.search + url.hash;
    } catch {
        const separator = rawUrl.includes('?') ? '&' : '?';
        return rawUrl.includes('v=') ? rawUrl : `${rawUrl}${separator}v=${INNER_BUILD_VERSION}`;
    }
};

interface ScreenGeometryFit {
    worldPosition: THREE.Vector3;
    worldQuaternion: THREE.Quaternion;
    widthMeters: number;
    heightMeters: number;
    worldNormal: THREE.Vector3;
}

const fitScreenMesh = (
    mesh: THREE.Mesh,
    cameraPos?: THREE.Vector3
): ScreenGeometryFit => {
    mesh.updateWorldMatrix(true, true);
    const geom = mesh.geometry as THREE.BufferGeometry;
    if (!geom.attributes.normal) geom.computeVertexNormals();

    const positionAttr = geom.attributes.position as THREE.BufferAttribute;
    const normalAttr = geom.attributes.normal as THREE.BufferAttribute;

    // Average vertex normal (in geometry-local space)
    const localNormal = new THREE.Vector3();
    const tmpN = new THREE.Vector3();
    for (let i = 0; i < normalAttr.count; i++) {
        tmpN.set(normalAttr.getX(i), normalAttr.getY(i), normalAttr.getZ(i));
        localNormal.add(tmpN);
    }
    if (localNormal.lengthSq() < 1e-6) {
        // Degenerate normals — fall back to world Z
        localNormal.set(0, 0, 1);
    }
    localNormal.normalize();

    // World-space normal (transformDirection ignores translation, applies rotation+scale)
    const worldNormal = localNormal.clone().transformDirection(mesh.matrixWorld).normalize();

    // If we can guess the camera direction, flip normal so it faces the camera
    if (cameraPos) {
        const meshWorldPos = new THREE.Vector3();
        mesh.getWorldPosition(meshWorldPos);
        const toCamera = new THREE.Vector3().subVectors(cameraPos, meshWorldPos).normalize();
        if (worldNormal.dot(toCamera) < 0) {
            worldNormal.multiplyScalar(-1);
        }
    }

    // Choose two orthonormal tangents in the plane defined by the screen normal.
    // Prefer keeping +Y axis as the screen's "up" when normal isn't vertical.
    const refUp =
        Math.abs(worldNormal.y) < 0.95
            ? new THREE.Vector3(0, 1, 0)
            : new THREE.Vector3(0, 0, 1);
    const tangentX = new THREE.Vector3().crossVectors(refUp, worldNormal).normalize();
    const tangentY = new THREE.Vector3().crossVectors(worldNormal, tangentX).normalize();

    // Project all world-space vertices onto the plane and find min/max along tangents
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const sumWorld = new THREE.Vector3();
    const v = new THREE.Vector3();
    for (let i = 0; i < positionAttr.count; i++) {
        v.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
        v.applyMatrix4(mesh.matrixWorld);
        sumWorld.add(v);
    }
    const worldCentroid = sumWorld.multiplyScalar(1 / positionAttr.count);

    for (let i = 0; i < positionAttr.count; i++) {
        v.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
        v.applyMatrix4(mesh.matrixWorld);
        v.sub(worldCentroid);
        const px = v.dot(tangentX);
        const py = v.dot(tangentY);
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
    }

    const widthMeters = Math.max(maxX - minX, 0.001);
    const heightMeters = Math.max(maxY - minY, 0.001);

    // Snap centroid back to the actual midpoint of the projected bbox
    const offsetX = (minX + maxX) / 2;
    const offsetY = (minY + maxY) / 2;
    const adjustedCenter = worldCentroid
        .clone()
        .addScaledVector(tangentX, offsetX)
        .addScaledVector(tangentY, offsetY);

    // Build orientation: +X = tangentX (right), +Y = tangentY (up), +Z = worldNormal (out)
    const m = new THREE.Matrix4().makeBasis(tangentX, tangentY, worldNormal);
    const finalQuat = new THREE.Quaternion().setFromRotationMatrix(m);

    return {
        worldPosition: adjustedCenter,
        worldQuaternion: finalQuat,
        widthMeters,
        heightMeters,
        worldNormal,
    };
};

export default class MonitorScreen extends EventEmitter {
    application: Application;
    scene: THREE.Scene;
    cssScene: THREE.Scene;
    iframe: HTMLIFrameElement | null = null;
    private iframeContainer: HTMLDivElement | null = null;
    private iframeFallback: HTMLDivElement | null = null;
    private iframeFallbackTimer: number | null = null;
    private iframeMessageListenerBound = false;
    private modelFreeMode = false;
    cssObject: CSS3DObject | null = null;
    occluder: THREE.Mesh | null = null;
    screenSizePixels = new THREE.Vector2(RASTER_W, RASTER_H);
    private fit: ScreenGeometryFit | null = null;
    private prevInComputer = false;
    private mouseClickInProgress = false;

    constructor() {
        super();
        this.application = new Application();
        this.scene = this.application.scene;
        this.cssScene = this.application.cssScene;
        this.bindGlobalMouseEvents();
        this.application.audio.on('muted', () => this.postControlState());
        this.bindIframeMessageListener();
    }

    getFit(): ScreenGeometryFit | null {
        return this.fit;
    }

    bindToScreenMesh(mesh: THREE.Mesh) {
        const cameraPos = this.application.camera.instance.position.clone();
        this.fit = fitScreenMesh(mesh, cameraPos);

        const aspect = this.fit.widthMeters / this.fit.heightMeters;
        const rasterAspect = RASTER_W / RASTER_H;
        if (aspect >= rasterAspect) {
            this.screenSizePixels.set(RASTER_W, Math.round(RASTER_W / aspect));
        } else {
            this.screenSizePixels.set(Math.round(RASTER_H * aspect), RASTER_H);
        }

        console.info(
            `[MonitorScreen] fit width=${this.fit.widthMeters.toFixed(3)}m`,
            `height=${this.fit.heightMeters.toFixed(3)}m`,
            `iframe ${this.screenSizePixels.x}x${this.screenSizePixels.y}px`,
            'pos=',
            this.fit.worldPosition.toArray().map((n) => n.toFixed(3)).join(','),
            'normal=',
            this.fit.worldNormal.toArray().map((n) => n.toFixed(2)).join(',')
        );

        this.replaceMeshAsOccluder(mesh);
        this.createIframe();
    }

    private replaceMeshAsOccluder(mesh: THREE.Mesh) {
        // FrontSide：从屏幕正面看，会写深度但不画颜色，正确遮住背后的 GL 物体；
        // 从背面看，整个面被剔除，跟相机背面隐藏 iframe 的逻辑互相配合。
        const m = new THREE.MeshBasicMaterial({
            color: 0x000000,
            opacity: 0,
            transparent: true,
            side: THREE.FrontSide,
            blending: THREE.NoBlending,
            depthWrite: true,
        });
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => mat.dispose?.());
        } else {
            (mesh.material as THREE.Material).dispose?.();
        }
        mesh.material = m;
        mesh.renderOrder = -1;
        this.occluder = mesh;
    }

    private createIframe() {
        if (!this.fit) return;

        const w = this.screenSizePixels.x;
        const h = this.screenSizePixels.y;

        const container = document.createElement('div');
        container.style.width = `${w}px`;
        container.style.height = `${h}px`;
        container.style.background = '#020405';
        container.style.overflow = 'hidden';
        container.style.position = 'relative';
        container.className = 'model-screen-container';
        this.iframeContainer = container;

        const iframe = document.createElement('iframe');
        iframe.style.width = `${w}px`;
        iframe.style.height = `${h}px`;
        iframe.style.border = 'none';
        iframe.style.display = 'block';
        iframe.style.position = 'absolute';
        iframe.style.inset = '0';
        iframe.id = 'computer-screen';
        iframe.title = 'Inner OS';
        iframe.allow = 'autoplay; fullscreen; clipboard-read; clipboard-write';
        iframe.allowFullscreen = true;
        iframe.frameBorder = '0';

        const configuredInnerUrl = process.env.INNER_URL || '';
        const defaultInnerUrl = '/inner/';
        const innerUrl =
            new URLSearchParams(window.location.search).get('inner') ||
            configuredInnerUrl ||
            defaultInnerUrl;
        iframe.src = withInnerBuildVersion(innerUrl);

        const fallback = document.createElement('div');
        fallback.style.position = 'absolute';
        fallback.style.inset = '0';
        fallback.style.zIndex = '2';
        fallback.style.display = 'flex';
        fallback.style.alignItems = 'center';
        fallback.style.justifyContent = 'center';
        fallback.style.flexDirection = 'column';
        fallback.style.gap = '12px';
        fallback.style.padding = '32px';
        fallback.style.boxSizing = 'border-box';
        fallback.style.background = '#14262e';
        fallback.style.color = '#eaf2f4';
        fallback.style.fontFamily = 'monospace';
        fallback.style.fontSize = '22px';
        fallback.style.textAlign = 'center';
        fallback.style.letterSpacing = '0';
        fallback.innerHTML = `
            <div>Loading screen content...</div>
            <div style="font-size: 16px; color: #afc0c7;">正在加载屏幕内容</div>
        `;
        this.iframeFallback = fallback;

        if (this.iframeFallbackTimer) {
            window.clearTimeout(this.iframeFallbackTimer);
        }
        this.iframeFallbackTimer = window.setTimeout(() => {
            this.showIframeFailure(innerUrl);
        }, 12000);

        this.bindIframeMessageListener();
        iframe.onload = () => {
            this.hideIframeFallback();
            this.postControlState();
        };
        iframe.onerror = () => this.showIframeFailure(innerUrl);

        container.appendChild(iframe);
        container.appendChild(fallback);
        this.iframe = iframe;

        const obj = new CSS3DObject(container);
        obj.position.copy(this.fit.worldPosition);
        obj.quaternion.copy(this.fit.worldQuaternion);

        // The CSS3D plane lives in pixel space; scale so its width in pixels
        // covers the mesh's world-space width in meters.
        const cssScale = this.fit.widthMeters / w;
        obj.scale.setScalar(cssScale);

        // Push the iframe a hair forward along the screen normal so it
        // doesn't z-fight with the occluder mesh.
        const tinyOffset = this.fit.worldNormal.clone().multiplyScalar(0.0008);
        obj.position.add(tinyOffset);

        this.cssScene.add(obj);
        this.cssObject = obj;
    }

    setModelFreeMode(enabled: boolean): boolean {
        const iframe = this.iframe;
        const container = this.iframeContainer;
        if (!iframe || !container) return false;
        if (enabled === this.modelFreeMode) return true;

        if (enabled) {
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.display = 'block';
            this.modelFreeMode = true;
            document.body.classList.add('model-free-mode');
            this.application.mouse.inComputer = true;
            this.prevInComputer = true;
            this.postControlState();
            return true;
        }

        iframe.style.width = `${this.screenSizePixels.x}px`;
        iframe.style.height = `${this.screenSizePixels.y}px`;
        iframe.style.display = 'block';
        this.modelFreeMode = false;
        document.body.classList.remove('model-free-mode');
        this.application.mouse.inComputer = false;
        this.prevInComputer = false;
        this.postControlState();
        return true;
    }

    isModelFreeMode() {
        return this.modelFreeMode;
    }

    private postControlState() {
        const iframe = this.iframe;
        if (!iframe?.contentWindow) return;
        iframe.contentWindow.postMessage(
            {
                type: 'outer-control-state',
                modelFreeMode: this.modelFreeMode,
                muted: this.application.audio.isMuted(),
            },
            '*'
        );
    }

    private bindIframeMessageListener() {
        if (this.iframeMessageListenerBound) return;
        window.addEventListener('message', this.onIframeMessage);
        this.iframeMessageListenerBound = true;
    }

    private hideIframeFallback() {
        if (this.iframeFallbackTimer) {
            window.clearTimeout(this.iframeFallbackTimer);
            this.iframeFallbackTimer = null;
        }
        if (this.iframeFallback) {
            this.iframeFallback.style.display = 'none';
        }
    }

    private showIframeFailure(innerUrl: string) {
        if (!this.iframeFallback) return;
        this.iframeFallback.style.display = 'flex';
        this.iframeFallback.innerHTML = `
            <div>Screen content failed to load.</div>
            <div style="font-size: 16px; line-height: 1.5; color: #afc0c7;">
                屏幕内容加载失败。请检查 inner 站点是否部署成功，以及 outer 的 INNER_URL 是否配置为：<br />
                <span style="color: #66bdeb;">${innerUrl}</span>
            </div>
        `;
    }

    private onIframeMessage = (event: MessageEvent) => {
        if (!event.data || typeof event.data !== 'object') return;
        const iframe = this.iframe;
        if (!iframe) return;

        const t = event.data.type;
        if (t === 'inner-ready') {
            this.hideIframeFallback();
            this.postControlState();
            return;
        }
        if (t === 'outer-control-request-state') {
            this.postControlState();
            return;
        }
        if (t === 'outer-control-toggle-model') {
            this.setModelFreeMode(!this.modelFreeMode);
            return;
        }
        if (t === 'outer-control-toggle-mute') {
            this.application.audio.setMuted(!this.application.audio.isMuted());
            this.postControlState();
            return;
        }
        if (t === 'mousemove' || t === 'mousedown' || t === 'mouseup') {
            const evt = new CustomEvent(t, { bubbles: true, cancelable: false });
            // @ts-ignore
            evt.inComputer = true;
            const rect = iframe.getBoundingClientRect();
            // @ts-ignore
            evt.clientX = Math.round((event.data.clientX ?? 0) + rect.left);
            // @ts-ignore
            evt.clientY = Math.round((event.data.clientY ?? 0) + rect.top);
            iframe.dispatchEvent(evt);
        }
    };

    /**
     * "进入"屏幕要精确命中 iframe；"离开"屏幕带 100px (~2.6cm @ 96 DPI) 滞后缓冲。
     * 这样在屏幕区域内不小心滑出几像素不会立刻退出 zoom 状态，
     * 只有真正越过模拟"显示器外边框 + 2cm"的范围才退出。
     */
    private static readonly LEAVE_BUFFER_PX = 200;

    private bindGlobalMouseEvents() {
        document.addEventListener('mousemove', (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (this.modelFreeMode) {
                this.application.mouse.inComputer = true;
                this.prevInComputer = true;
                return;
            }
            const id = target ? target.id : '';
            // @ts-ignore
            const inComputerFlag = (event as { inComputer?: boolean }).inComputer === true;
            const directHit = id === 'computer-screen' || inComputerFlag;

            let inComputer: boolean;
            if (directHit) {
                inComputer = true;
            } else if (this.prevInComputer && this.iframe) {
                const r = this.iframe.getBoundingClientRect();
                const buf = MonitorScreen.LEAVE_BUFFER_PX;
                const within =
                    event.clientX >= r.left - buf &&
                    event.clientX <= r.right + buf &&
                    event.clientY >= r.top - buf &&
                    event.clientY <= r.bottom + buf;
                inComputer = within;
            } else {
                inComputer = false;
            }

            this.application.mouse.inComputer = inComputer;

            if (inComputer && !this.prevInComputer) this.trigger('enterMonitor');
            if (!inComputer && this.prevInComputer && !this.mouseClickInProgress) {
                this.trigger('leftMonitor');
            }
            this.prevInComputer = inComputer;
        });

        document.addEventListener('mousedown', () => {
            this.mouseClickInProgress = true;
        });
        document.addEventListener('mouseup', () => {
            this.mouseClickInProgress = false;
        });
    }

    update() {
        if (!this.cssObject || !this.fit) return;
        // Keep the real iframe pinned to the monitor. The old normal-dot
        // cull could hide the CSS3D screen from the front on some model fits,
        // leaving only the teal WebGL glow plane visible.
        this.cssObject.visible = true;
    }
}
